import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useOutsideClicked from './useOutsideClicked';
import { useTranslation } from '../hooks/useTranslation';
function flattenOptions(options) {
    return options.flatMap(item =>
        item.options ? [{ ...item, disabled: true }, ...flattenOptions(item.options)] : [item]
    );
}

/**
 * Accessible dropdown select built on the ARIA combobox + listbox pattern.
 *
 * Supports flat option lists and grouped options (one level of nesting).
 * Integrates with react-hook-form via `register`/`setValue`, or with plain
 * state via `onChange`. Closes on outside click and returns focus to the
 * trigger button on close.
 *
 * Keyboard navigation (trigger and open list):
 *   ArrowDown / ArrowUp — move focus, skipping group headers
 *   Home / End          — jump to first / last item
 *   Enter / Space       — open dropdown or select focused item
 *   Escape              — close dropdown
 *
 * @param {Object}   props
 * @param {Array}    props.options
 *   Flat or grouped option list. Each item is one of:
 *   - `{ value: string, label?: string, disabled?: boolean }` — a selectable option
 *   - `{ value: string, label: string, options: OptionItem[], group: true }` — a group header
 *     whose children are rendered beneath it (header itself is not selectable).
 *
 * @param {string}   [props.selectedValue]
 *   Controlled selected value. When provided the component syncs its internal
 *   state to this value on every change.
 *
 * @param {function} [props.onChange]
 *   Called with the newly selected value (or `null` when deselected).
 *   Use this for uncontrolled / plain-state usage.
 *
 * @param {function} [props.setValue]
 *   react-hook-form `setValue`. Used together with `register` and `fieldName`
 *   as an alternative to `onChange`.
 * @param {function} [props.register]
 *   react-hook-form `register`. Renders a hidden `<input>` to participate in
 *   form validation. Requires `fieldName`.
 * @param {string}   [props.fieldName]
 *   Field name passed to `register` and `setValue`.
 *
 * @param {string}   props.id
 *   Base ID used to generate stable ARIA IDs for the combobox, listbox, and
 *   individual options (`${id}-combobox`, `${id}-listbox`, `option-${value}`).
 *
 * @param {string}   [props.label]
 *   Accessible name for the combobox trigger (`aria-label`). Falls back to the
 *   translated word "Hour" if omitted.
 * @param {string}   [props.placeholder]
 *   Text shown inside the trigger when nothing is selected.
 *
 * @param {function} [props.renderOption]
 *   `(item, isSelected) => ReactNode` — custom renderer for each list item.
 *   Falls back to `item.value` when omitted.
 * @param {function} [props.renderHeader]
 *   `() => ReactNode` — optional slot rendered at the top of the open dropdown,
 *   above all options (e.g. a search input or banner).
 *
 * @param {string}   [props.className]        Extra class on the outer wrapper `div`.
 * @param {string}   [props.classNameButton]  Extra class on the combobox trigger element.
 * @param {string}   [props.classNameList]    Extra class on the listbox `div`.
 */
const CustomSelect = React.forwardRef(function CustomSelect({
    options,
    setValue,
    register,
    fieldName,
    renderOption,
    renderHeader,
    onChange,
    label,
    placeholder,
    id,
    className = '',
    classNameButton = '',
    classNameList = '',
    classNameGroup = '',
    selectedValue = null
}, forwardedRef) {
    const { t } = useTranslation();
    const flatOptions = useMemo(() => flattenOptions(options), [options]);
    const wrapperRef = useRef(null);
    const dropdownButtonRef = useRef(null);
    // Sync the forwarded ref to the same DOM node as dropdownButtonRef so that
    // react-hook-form's shouldFocusError can call .focus() on this element.
    const setTriggerRef = useCallback((node) => {
        dropdownButtonRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
    }, [forwardedRef]);
    const [isOpen, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(selectedValue);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const nextEnabled = useCallback((from, dir) => {
        let i = from + dir;
        while (i >= 0 && i < flatOptions.length) {
            if (!flatOptions[i]?.disabled) return i;
            i += dir;
        }
        return from;
    }, [flatOptions]);

    const skipGroups = useCallback((index, dir) => {
        let i = index;
        while (i >= 0 && i < flatOptions.length && flatOptions[i]?.disabled) i += dir;
        return (i >= 0 && i < flatOptions.length) ? i : index;
    }, [flatOptions]);

    // useOutsideClicked calls this on every outside click, even when already
    // closed. Focusing unconditionally used to auto-scroll the trigger into
    // view on any page click - only refocus if we were actually open.
    const close = useCallback(() => {
        setOpen(prevOpen => {
            if (prevOpen) dropdownButtonRef.current?.focus();
            return false;
        });
    }, [dropdownButtonRef]);

    useOutsideClicked(wrapperRef, dropdownButtonRef, close);

    const handleItemClick = useCallback((value) => {
        setSelectedItem(prev => (prev === value ? null : value));
        close();
    }, [close]);

    const handleTriggerKeyDown = (e) => {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (!isOpen) {
                    setOpen(true);
                } else {
                    if (focusedIndex >= 0 && !flatOptions[focusedIndex]?.disabled) handleItemClick(flatOptions[focusedIndex].value);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) { setOpen(true); break; }
                setFocusedIndex(i => skipGroups(Math.min(i + 1, flatOptions.length - 1), 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(i => skipGroups(Math.max(i - 1, 0), -1));
                break;
            case 'Home':
                e.preventDefault();
                setFocusedIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setFocusedIndex(flatOptions.length - 1);
                break;
            case 'Escape':
                e.preventDefault();
                close();
                break;
            default:
                break;
        }
    };

    // Scroll the active option into view whenever focusedIndex changes.
    useEffect(() => {
        if (!isOpen || focusedIndex < 0) return;
        const optionEl = document.getElementById(`option-${flatOptions[focusedIndex]?.value}`);
        optionEl?.scrollIntoView({ block: 'nearest' });
    }, [focusedIndex, isOpen, flatOptions]);

    useEffect(() => {
        setFocusedIndex(-1);
    }, [options]);

    // Announce the active item separately from the expanded-state announcement.
    // NVDA swallows aria-activedescendant when aria-expanded changes in the same render.
    useEffect(() => {
        if (!isOpen) { setFocusedIndex(-1); return; }
        setFocusedIndex(
                selectedItem
                    ? Math.max(0, flatOptions.findIndex(o => o.value === selectedItem))
                    : nextEnabled(-1, 1)
            );
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSelectedItem(selectedValue ?? null);
    }, [selectedValue]);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const setValueRef = useRef(setValue);
    setValueRef.current = setValue;

    useEffect(() => {
        if (onChangeRef.current) onChangeRef.current(selectedItem);
        else if (setValueRef.current) setValueRef.current(fieldName, selectedItem);
    }, [selectedItem, fieldName]);

    const labelId = `${id}-select-label`;
    const listboxId = `${id}-listbox`;
    const triggerId = `${id}-combobox`;
    const activeDescendant =
        isOpen && focusedIndex >= 0
            ? `option-${flatOptions[focusedIndex]?.value}`
            : undefined;

    const resolvedPlaceholder = placeholder;
    const selectedOption = selectedItem != null ? flatOptions.find(o => o.value === selectedItem) : null;
    const displayValue = selectedOption
        ? (selectedOption.label ?? selectedOption.value)
        : resolvedPlaceholder;

    const handleTriggerClick = () => {
        setOpen(o => !o);
    }

    const handleListKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(i => skipGroups(Math.min(i + 1, flatOptions.length - 1), 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(i => skipGroups(Math.max(i - 1, 0), -1));
                break;
            case 'Home':
                e.preventDefault();
                setFocusedIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setFocusedIndex(flatOptions.length - 1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (focusedIndex >= 0 && !flatOptions[focusedIndex]?.disabled) handleItemClick(flatOptions[focusedIndex].value);
                break;
            case 'Escape':
                e.preventDefault();
                close();
                break;
            default:
                break;
        }
    }

    function renderOptions(options, selectedItem, focusedIndex, flatOptions) {
        return options.map((item, index) => {
            if (item.options) {
                return (
                    <div key={item.value} role="group" aria-label={item.label} className={`${classNameGroup}-${index + 1}`}>
                        <div className="dropdown-item dropdown-item--header h5 m-0">{item.label}</div>
                        {renderOptions(item.options, selectedItem, focusedIndex, flatOptions)}
                    </div>
                );
            }
            const idx = flatOptions.findIndex(o => o.value === item.value);
            const isSelected = selectedItem === item.value;
            const isFocused = focusedIndex === idx;
            return (
                <div
                    role="option"
                    id={`option-${item.value}`}
                    key={item.value}
                    className={`dropdown-item ${isSelected ? ' selected' : ''}${isFocused ? ' focused' : ''}${item.disabled ? ' disabled' : ''}${item.group ? ' dropdown-item--header' : ''}`}
                    aria-selected={item.disabled ? undefined : isSelected}
                    tabIndex={-1}
                    onClick={() => !item.disabled && handleItemClick(item.value)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                >
                    {renderOption ? renderOption(item, isSelected) : item.value}
                </div>
            );
        });
    }


    return (
        <div ref={wrapperRef} className={`dropdown dropdown-select ${className}`}>

            {register && (
                <input
                    {...register(fieldName, { required: t('Required') })}
                    // Not type="hidden" — hidden inputs can't be focused, so
                    // RHF's shouldFocusError would call .focus() and silently fail.
                    // This input is visually hidden but focusable; when RHF lands
                    // here it immediately redirects focus to the visible combobox.
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
                    tabIndex={-1}
                    aria-hidden="true"
                    onFocus={() => dropdownButtonRef.current?.focus()}
                />
            )}
            <div
                id={triggerId}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-label={label ?? t('Hour')}
                aria-activedescendant={activeDescendant ?? undefined}
                tabIndex={0}
                ref={setTriggerRef}
                className={`dropdown-header${isOpen ? ' open' : ''} ${classNameButton}`}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
            >
                {displayValue}
            </div>

            <div
                role="listbox"
                id={listboxId}
                aria-hidden={!isOpen}
                className={`dropdown-body ${isOpen ? ' open' : ''} ${classNameList}`}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onFocus={() => dropdownButtonRef.current?.focus()}
                onKeyDown={handleListKeyDown}
            >
                {renderHeader && renderHeader()}
                {renderOptions(options, selectedItem, focusedIndex, flatOptions)}
            </div>
        </div>
    );
});

export default CustomSelect;
